import {
  memberProfileModalPathsWithState,
  settingsNavPathV3,
} from "@circle-react/helpers/urlHelpers";
import type { PathWithState } from "@circle-react/helpers/urlHelpers/memberProfileModalPaths";

import { t } from "@/i18n-js/instance";

import { DrawerMenuItem } from "../components/DrawerMenuItem";
import { NestedDrawerMenuItem } from "../components/NestedDrawerMenuItem";
import { MobileAppearanceDrawerContent } from "../MobileAppearanceDrawerContent";
import { MobileCommunitySwitcherDrawerContent } from "../MobileCommunitySwitcherDrawerContent";

interface SettingsNavigationSectionProps {
  canAccessAuthentication?: boolean;
  canAccessBilling?: boolean;
  canShowAffiliate?: boolean;
  canSwitchCommunities?: boolean;
  closeAll: () => void;
  isAdmin: boolean;
  isAppearanceOpen: boolean;
  isCommunitySwitcherOpen: boolean;
  isModeratorButNotAdmin: boolean;
  location: { pathname: string; search: string };
  navigateAndClose: (path: string | PathWithState) => void;
  setIsAppearanceOpen: (open: boolean) => void;
  setIsCommunitySwitcherOpen: (open: boolean) => void;
}

/**
 * Nav drawer's "Settings & navigation" section.
 */
export const SettingsNavigationSection = ({
  isAppearanceOpen,
  setIsAppearanceOpen,
  isCommunitySwitcherOpen,
  setIsCommunitySwitcherOpen,
  canAccessAuthentication,
  canAccessBilling,
  canShowAffiliate,
  canSwitchCommunities,
  isAdmin,
  isModeratorButNotAdmin,
  closeAll,
  navigateAndClose,
  location,
}: SettingsNavigationSectionProps) => (
  <div className="flex flex-col gap-0.5">
    {/* Appearance - controlled nested child drawer */}
    <NestedDrawerMenuItem
      icon="sun"
      label={t("mobile_shell.appearance")}
      ariaLabel={t("mobile_shell.appearance")}
      open={isAppearanceOpen}
      onOpenChange={setIsAppearanceOpen}
    >
      <MobileAppearanceDrawerContent onClose={closeAll} />
    </NestedDrawerMenuItem>

    {/* Switch community - controlled nested child drawer. Hidden when the
        community has the switcher disabled (nothing actionable inside). */}
    {canSwitchCommunities && (
      <NestedDrawerMenuItem
        icon="shuffle"
        label={t("mobile_shell.switch_community")}
        ariaLabel={t("mobile_shell.switch_community")}
        open={isCommunitySwitcherOpen}
        onOpenChange={setIsCommunitySwitcherOpen}
      >
        <MobileCommunitySwitcherDrawerContent onClose={closeAll} />
      </NestedDrawerMenuItem>
    )}

    {canAccessAuthentication && (
      <DrawerMenuItem
        icon="lock"
        label={t("mobile_shell.manage_authentication")}
        onClick={() =>
          navigateAndClose(
            memberProfileModalPathsWithState.authentication(location),
          )
        }
      />
    )}
    {canAccessBilling && (
      <DrawerMenuItem
        icon="credit-card"
        label={t("billing")}
        onClick={() =>
          navigateAndClose(memberProfileModalPathsWithState.billing(location))
        }
      />
    )}
    {canShowAffiliate && (
      <DrawerMenuItem
        icon="affiliates"
        label={t("member_profile_modal.affiliate_dashboard.title")}
        onClick={() =>
          navigateAndClose(memberProfileModalPathsWithState.affiliate(location))
        }
      />
    )}

    {/* Admin settings - full page (not a nested drawer): see AdminSettingsContent */}
    {isAdmin && (
      <DrawerMenuItem
        icon="settings-gear"
        label={t("mobile_shell.admin_settings")}
        onClick={() => navigateAndClose("/admin-settings")}
      />
    )}

    {/* Moderators (non-admin) get desktop's reduced Logo menu: Content
        + Moderation, flat - no Admin Settings page for this role. */}
    {isModeratorButNotAdmin && (
      <>
        <DrawerMenuItem
          icon="page"
          label={t("settings_nav_v3.content.label")}
          onClick={() => navigateAndClose(settingsNavPathV3.content.posts)}
        />
        <DrawerMenuItem
          icon="shield-check"
          label={t("sidebar_moderation")}
          onClick={() => navigateAndClose("/settings/moderation")}
        />
      </>
    )}
  </div>
);
